import { createFileRoute, Link } from '@tanstack/react-router'
import { ListIcon, TruckIcon, UserIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRestocks } from '@/api/restocks'
import type { Restock } from '@/api/restocks'
import { VoidedBadge } from '@/components/corrections/voided'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney } from '@/components/materials/pool-format'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Restocks list (UI-SCREENS M-04, #66): date, vendor, line count, total and who recorded it,
// filtered by month. Owner and Staff both see it and both may add one (permissions.md).
export const Route = createFileRoute('/w/$slug/materials/restocks/')({
  component: RestocksList,
  staticData: { breadcrumb: 'restocks.title' },
})

function RestocksList() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const [month, setMonth] = useState('')
  const restocks = useRestocks(slug, { month: month || undefined })

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('restocks.title')}</h1>
        <Button asChild className="h-11">
          <Link to="/w/$slug/materials/restocks/new" params={{ slug }}>
            {t('restocks.new')}
          </Link>
        </Button>
      </div>

      <div className="flex flex-col gap-2 sm:max-w-xs">
        <Label htmlFor="month">{t('restocks.filterMonth')}</Label>
        <Input id="month" type="month" className="h-11" value={month} onChange={(event) => setMonth(event.target.value)} />
      </div>

      {restocks.isPending ? (
        <LoadingSkeleton />
      ) : restocks.isError || !restocks.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : restocks.data.length === 0 ? (
        <RecordListEmpty>{t(month ? 'restocks.emptyMonth' : 'restocks.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {restocks.data.map((restock) => (
            <RestockRow key={restock.id} slug={slug} restock={restock} />
          ))}
        </RecordList>
      )}
    </section>
  )
}

function RestockRow({ slug, restock }: { slug: string; restock: Restock }) {
  const { t } = useTranslation()

  return (
    <RecordListItem
      voided={Boolean(restock.voided_at)}
      title={
        <RecordLink to="/w/$slug/materials/restocks/$restockId" params={{ slug, restockId: String(restock.id) }}>
          {formatBusinessDate(restock.occurred_on)}
        </RecordLink>
      }
      badges={<VoidedBadge record={restock} />}
      meta={[
        restock.vendor ? { icon: TruckIcon, label: t('restocks.columns.vendor'), value: restock.vendor } : null,
        { icon: ListIcon, label: t('restocks.columns.lines'), value: t('restocks.lineCount', { count: restock.lines.length }) },
        restock.created_by
          ? {
              icon: UserIcon,
              label: t('restocks.columns.createdBy'),
              value: restock.created_by.name ?? restock.created_by.email,
            }
          : null,
      ]}
      figure={`Rp ${formatMoney(restock.total)}`}
    />
  )
}
